import {ReactElement, useEffect, useMemo, useState} from "react";
import {useCreateCustomDriversMutation, useQueryCustomItemsLazyQuery, VersionType} from "../../../__generated__/generated_types";
import {useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {Property} from "../../../contexts/properties/logic";
import {Button} from "@zendeskgarden/react-buttons";
import {toast} from "react-toastify";
import {TooltipModal} from "@zendeskgarden/react-modals";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../constants/Colors";
import {CustomZDDropdown} from "../../../atoms/custom-zd-dropdown/CustomZDDropdown";

export interface CreateCustomDriverTooltipModalProps {
    budgetYear: number;
    versionType: VersionType.Budget | VersionType.Reforecast;
    properties: Property[];
    locksLookup: Set<string>;
    referenceElement: HTMLButtonElement;
    onClose: () => void;
    onCreated: () => void;
}

export function CreateCustomDriverTooltipModal(props: CreateCustomDriverTooltipModalProps): ReactElement {
    const {budgetYear, versionType, properties, locksLookup, referenceElement, onClose, onCreated} = props;

    const [createCustomDrivers, {loading: createLoading}] = useCreateCustomDriversMutation();
    const [queryCustomItems, {data: customItemsData, loading: customItemsLoading}] = useQueryCustomItemsLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [selectedCustomItemId, setSelectedCustomItemId] = useState<string | undefined>(undefined);
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
    const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(undefined);

    const {accounts} = useChartOfAccounts();

    useEffect(() => {
        queryCustomItems();
    }, [queryCustomItems]);

    const customItems = useMemo(
        () => (customItemsData?.queryCustomItems ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
        [customItemsData]
    );
    const noCustomItemsAvailable = !customItemsLoading && customItems.length === 0;

    const unlockedProperties = useMemo(() => properties.filter(p => !locksLookup.has(p.id)),[properties, locksLookup]);
    const allPropertiesLocked = unlockedProperties.length === 0;

    function handleSubmit(): void {
        if (!selectedCustomItemId || selectedPropertyIds.length === 0 || !selectedAccountId) {
            toast.error("Please fill in all fields");
            return;
        }

        createCustomDrivers({
            variables: {
                budgetYear,
                versionTypes: [versionType],
                propertyIds: selectedPropertyIds,
                destinationAccountId: selectedAccountId,
                customItemId: selectedCustomItemId
            }
        }).then(res => {
            if (res.data?.createCustomDrivers) {
                toast.success("Custom driver created successfully");
                onCreated();
            } else {
                toast.error("Failed to create custom driver");
            }
        }).catch(() => {
            toast.error("Failed to create custom driver");
        });
    }

    return (
        <TooltipModal
            referenceElement={referenceElement}
            onClose={onClose}
            placement="start-top"
        >
            <TooltipModal.Title>Create Custom Driver</TooltipModal.Title>
            <TooltipModal.Body>
                <div style={{display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: "20rem"}}>
                    <CustomZDDropdown
                        label="Custom Item"
                        options={customItems.map(item => ({value: item.id, label: item.name}))}
                        initialSelectedItems={selectedCustomItemId ? [selectedCustomItemId] : []}
                        applySelectedItems={items => setSelectedCustomItemId(items[0])}
                        openDropdownPlaceholder="Search custom items..."
                        closedDropdownPlaceholder={customItemsLoading ? "Loading custom items..." : "Select a custom item"}
                        isError={undefined}
                    />
                    <CustomZDDropdown
                        label="Properties"
                        allOption
                        isMulti
                        options={unlockedProperties.map(p => ({value: p.id, label: p.name}))}
                        initialSelectedItems={selectedPropertyIds}
                        applySelectedItems={items => setSelectedPropertyIds(items)}
                        openDropdownPlaceholder="Search properties..."
                        closedDropdownPlaceholder="Select properties"
                        isError={undefined}
                    />
                    <CustomZDDropdown
                        label="Destination Account"
                        options={accounts.map(a => ({value: a.id, label: `${a.accountNumber} - ${a.name}`}))}
                        initialSelectedItems={selectedAccountId ? [selectedAccountId] : []}
                        applySelectedItems={items => setSelectedAccountId(items[0])}
                        openDropdownPlaceholder="Search accounts..."
                        closedDropdownPlaceholder="Select an account"
                        isError={undefined}
                    />
                    {noCustomItemsAvailable &&
                        <span style={{color: COLORS.NEGATIVE_COLOR, fontSize: "0.85rem"}}>
                            No custom items are configured. Add custom items in Admin before creating a custom driver.
                        </span>
                    }
                    {allPropertiesLocked &&
                        <span style={{color: COLORS.NEGATIVE_COLOR, fontSize: "0.85rem"}}>
                            All properties are locked for the selected forecast period.
                        </span>
                    }
                    <Button
                        isPrimary
                        size="small"
                        onClick={handleSubmit}
                        disabled={allPropertiesLocked || noCustomItemsAvailable || customItemsLoading || createLoading || !selectedCustomItemId || selectedPropertyIds.length === 0 || !selectedAccountId}
                    >
                        {createLoading
                            ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading" />
                            : <span>Create</span>
                        }
                    </Button>
                </div>
            </TooltipModal.Body>
            <TooltipModal.Close aria-label="Close" />
        </TooltipModal>
    );
}
